// -------------------------------
// ASSET CATEGORY INTERFACES
// -------------------------------
export interface PendingAssetCategory {
    name?: string;
    status?: string; // active | inactive
}

export interface PendingDeleteAssetCategory {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE ASSET CATEGORY — START
// -------------------------------
export const startCreateAssetCategoryFlow = (
    setPendingAssetCategory: (data: PendingAssetCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        setPendingAssetCategory({ name: nameFromIntent });

        pushAIChat(`Should the asset category "${nameFromIntent}" be active or inactive?`);
        speak(`Should the asset category ${nameFromIntent} be active or inactive?`);
        return;
    }

    setPendingAssetCategory({});
    pushAIChat("Sure! What should the asset category be called?");
    speak("What should the asset category be called?");
};

// -------------------------------
// CREATE ASSET CATEGORY — PROCESS
// -------------------------------
export const processPendingAssetCategory = (
    text: string,
    pending: PendingAssetCategory,
    setPendingAssetCategory: (data: PendingAssetCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // STEP 1 — Category Name
    if (!pending.name) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid asset category name.");
            speak("Please provide a valid asset category name.");
            return;
        }

        setPendingAssetCategory({ ...pending, name: cleanName });

        pushAIChat("Should this category be active or inactive?");
        speak("Should this category be active or inactive?");
        return;
    }

    // STEP 2 — Status (FINAL)
    if (!pending.status) {
        const status = text.toLowerCase().includes("inactive")
            ? "inactive"
            : "active";

        pushAIChat(`Creating asset category "${pending.name}"...`);
        speak(`Creating asset category ${pending.name}.`);

        window.dispatchEvent(
            new CustomEvent("ai-create-asset-category", {
                detail: {
                    name: pending.name,
                    status
                }
            })
        );

        setPendingAssetCategory(null);
    }
};

// -------------------------------
// DELETE ASSET CATEGORY — START
// -------------------------------
export const startDeleteAssetCategoryFlow = (
    setPendingDelete: (data: PendingDeleteAssetCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting asset category "${nameFromIntent}"...`);
        speak(`Deleting asset category ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-asset-category", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which asset category would you like to delete?");
    speak("Which asset category would you like to delete?");
};

// -------------------------------
// DELETE ASSET CATEGORY — PROCESS
// -------------------------------
export const processPendingDeleteAssetCategory = (
    text: string,
    pending: PendingDeleteAssetCategory,
    setPendingDelete: (data: PendingDeleteAssetCategory | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (pending.awaitingName) {
        const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

        if (!cleanName) {
            pushAIChat("Please provide a valid asset category name.");
            speak("Please provide a valid asset category name.");
            return;
        }

        pushAIChat(`Deleting asset category "${cleanName}"...`);
        speak(`Deleting asset category ${cleanName}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-asset-category", {
                detail: { name: cleanName }
            })
        );

        setPendingDelete(null);
    }
};

